// Single source of truth for the app's visible version. Bumped per
// AAB release (see /app/memory/RELEASE_NOTES_CLOSED_TESTING.md).
// The footer chip and the WhatsNewToast both read from here so a
// version bump is a one-line change.
//
// `notes` is the short, user-facing changelog shown in the toast on
// first launch after the bump. Keep each bullet ≤ 70 chars so the
// modal stays readable on a phone screen.
export const APP_VERSION = "5.2";

export const APP_RELEASE_NOTES = {
  "5.2": [
    "Messages: TAP A NAME TO CHAT — every member listed, tap to talk instantly",
    "New messages appear live in the chat, no refreshing needed",
    "Your app now force-updates itself after every new release",
    "Block + Private mode fully live",
  ],
  "5.1": [
    "Messages is now simple chat: tap a name and type, just like WhatsApp",
    "New messages pop up live in the chat — no refreshing",
    "Friend requests land in Messages with one-tap Accept",
    "Block button in every chat + a Private mode in profile settings",
  ],
  "5.0": [
    "New: a guided walkthrough for first-time users — replay it anytime from Settings",
    "Password fields now have a show/hide eye icon so you can check what you typed",
    "Added a 'Rate this app' button in Settings",
    "Nightly Lord's Prayer now arrives by email if texts aren't available yet",
    "Trending player zoom fixed so songs aren't cropped too tight",
    "Smarter voice search — 'John 1:1' is understood even with speech hiccups",
  ],
  "4.0": [
    "Lives tab now reads directly from your broadcast history — every past Go Live shows up",
    "Works retroactively: broadcasts you did earlier this week will appear once you load the page",
    "Active LIVE broadcasts still bubble to the top with a pulsing red badge",
    "Channel-only broadcasts marked with 'channel only' label on the card",
  ],
  "3.9": [
    "Go Live now has a 'Visible to' toggle — Everyone (homepage rail + your channel) or My channel only",
    "Channel-only broadcasts skip the public Live Now rail, perfect for followers-only sessions",
    "Your channel page still shows the LIVE banner either way",
  ],
  "3.8": [
    "Go Live: simpler, bulletproof flow — tap End Stream and you're done",
    "Channel shows '📺 Was Live earlier' as a permanent record of every broadcast",
    "No more file pickers or upload steps to wrestle with",
    "If you want replays later, LiveKit Egress (~30¢/hr) records to your channel automatically",
  ],
  "3.7": [
    "Live replays — after End Stream, attach a video from your phone and it plays on your channel's Lives tab",
    "Channel-only: replays NEVER post to Congregation Live (we hear you)",
    "Skip the attach step to just keep the '📺 Was Live earlier' marker on your channel",
    "Same proven Cloudinary upload pipeline that already works for your wall videos",
  ],
  "3.6": [
    "Go Live polished — tap End Stream and your broadcast saves cleanly to your channel as '📺 Was Live earlier'",
    "Stuck 'broadcasting now' banner self-heals after 6h (server-side watchdog runs every 10 min)",
    "End-on-tab-close now uses fetch keepalive with proper auth — no more lingering live status",
    "Discord-style voice/video chat in study rooms with emoji reactions and synthesized sound pack",
    "Faster room list, friend list, and notifications via new indexes",
  ],
  "3.5": [
    "End Stream now opens the EXACT same wall composer you use to share videos — no more custom modal",
    "Tap End Stream → broadcast saved on your channel + composer opens to post a clip if you want",
    "Stuck 'broadcasting now' banner finally fixed — auto-closes after 6h if your browser drops mid-stream",
    "End-on-tab-close fixed too (auth token wasn't included in the old sendBeacon — now uses fetch keepalive)",
  ],
  "3.4": [
    "Live broadcasts now use the SAME proven flow as posting a video — no more flaky in-browser recording",
    "After End Stream, pick a clip from your phone's camera roll to attach as the replay",
    "Skip the clip if you just want to log that you went live — broadcast still saves as '📺 Was Live'",
    "Inline replay modal plays the attached clip on every device",
  ],
  "3.3": [
    "Live broadcasts are now RECORDED — tap any saved broadcast on your channel to replay it",
    "Upload progress shows on the End Stream → Post to channel button",
    "Lives tab cards: ▶ Tap to replay (with recording) · 📺 Recording not available (older broadcasts)",
    "Inline replay modal with full video controls — no navigation needed",
  ],
  "3.2": [
    "Go Live now posts to your channel — Home banner + Lives tab show LIVE NOW in red",
    "After ending, the broadcast stays on your channel as 📺 Was Live earlier",
    "Lives tab counter (Lives · 3) matches your real broadcast history",
    "Discord-style voice & video chat in every Study Room — mic, camera, raise hand",
    "Emoji reactions float across everyone's screen — 10 reactions with synthesized sounds",
    "Sound pack: 🙏 Amen chime, ✝️ cathedral bell, ❤️ heartbeat, 👏 clap, 🎉 horn + more",
    "Camera toggle is optional — defaults OFF for privacy, on with one tap",
    "Mobile menu now shows the red Go Live button (was desktop-only)",
    "Cleaner test card cleanup — stuck broadcasts auto-close after 24h",
    "Rooms list now ~4× faster · friends + notifications indexed for snappy scrolling",
  ],
  "3.1": [
    "Themed Puzzle Packs — 12 collections (Advent, Easter, Psalms, Parables…) on /crossword",
    "Library grew to 386 puzzles — 60 new themed crosswords & word searches",
    "Holy Letters — brand-new Scrabble-style game (3rd tab in /crossword)",
    "Pass-and-play with 2–4 humans, or Solo vs AI on Easy / Medium / Hard",
    "15×15 classic + 11×11 express boards, with 5× bonus on Biblical words",
    "Blank tiles, tile-swap, hint button (3 per game), and a top-plays leaderboard",
    "Every channel can Go Live — surfaced even before creator activation",
    "LIVE NOW badge + one-tap Watch button appear when a creator starts broadcasting",
    "All 3 puzzle games (Crossword / Word Search / Holy Letters) translate to 18 languages",
    "Bio updated to read 'laugh with clean comedy' — broader than Christian-only",
  ],
  "2.7": [
    "Sign in stays put — refresh the page and you stay logged in for 30 days",
    "Google sign-in fails fast (4 sec max) with a clear retry button instead of hanging",
    "Login page now remembers your preferred sign-in method — one tap to come back",
    "Daily Devotional + 3 home cards read aloud instantly — audio is pre-warmed at startup",
    "Recipes translate every word into all 18 languages, including category labels",
    "Testified Tuesday renamed to Testimony Tuesday on the daily rhythm",
    "Home loads 44× less data — catalog rails ship only what they show",
    "Works offline — last sermon, song, and home content stay available without signal",
  ],
  "2.6": [
    "Even smaller scroll arrows (5px) — they don't block any text now",
    "Today's worship card now updates in your local timezone — no more seeing yesterday's theme",
    "Sunday Dinner is pinned to the top scroll rail",
  ],
  "2.5": [
    "Login is now lightning fast even under heavy traffic — no more timeouts when many people are using the app at once",
    "Password checks moved to a background thread so translations don't slow down sign-in",
    "All AI translation calls have a strict 12s timeout so a slow call can't lock up the server",
  ],
  "2.4": [
    "All Sunday Dinner recipes now translate into every supported language",
    "Read Aloud already speaks recipes in your language — now the printed text matches",
    "Switch languages and the recipe re-renders ingredients, instructions, and tips automatically",
  ],
  "2.3": [
    "Recipes now have a Judgment thread (YouTube-style comments) — share your tips and read what others did",
    "Like your favorite recipes with the new ❤ button — count shows so the most-loved ones bubble up",
    "Sunday Dinner is now on the top scroll rail — one tap from any page",
  ],
  "2.2": [
    "Sunday Dinner Recipes is now the headliner — 70 classic recipes",
    "Every recipe plays its cooking video right inside the app (no leaving for YouTube)",
    "New categories: Soul Food, Mains, Sides, Breads, Desserts, Drinks, Breakfast",
    "Sunday Dinner rail added to the Home page so it's never more than a tap away",
    "Read recipes aloud while you cook in any of 18 languages",
  ],
  "2.1": [
    "New: Sunday Dinner Recipes — 20 classic soul food recipes",
    "Tap any recipe for ingredients, step-by-step instructions, and a cooking video link",
    "Generate any recipe with AI — just type what you want to cook",
    "Read recipes aloud while you cook (hands-free)",
  ],
  "2.0": [
    "Big v2 milestone — the app is now faster, calmer, and easier to live in",
    "Read Aloud speaks the bio in all 18 languages — translates first, then reads",
    "Pages always jump to the top on every nav — no more landing on the footer",
    "Smaller scroll arrows that don't block the text",
    "Gray brand footer now lives on the Home page only (no flashing during scroll)",
    "Faster bio loading + pre-warmed audio for instant playback",
  ],
  "1.0.9": [
    "Read Aloud now speaks the bio in all 18 languages — translates first, then reads",
    "Pages always jump to the top on every nav — no more landing on the footer",
    "Smaller scroll arrows that don't block the text",
    "Gray brand footer now lives on the Home page only (no flashing during scroll)",
    "Faster bio loading + pre-warmed audio for instant playback",
  ],
  "1.0.8": [
    "Way snappier — pages you've already visited come back instantly (30-second smart cache)",
    "Translation requests no longer flood the network — capped at 4 in flight so the rest of the app stays fast",
  ],
  "1.0.7": [
    "Bible, Precepts, Dictionary, and Concordance now fully translate to your language — headings, chips, counts, and every entry",
    "Lazy translation — only what's on screen translates, scrolling reveals more in your language",
  ],
  "1.0.6": [
    "Way faster language switching — translations land in ~1 second instead of 8-25",
    "Holiday banner verse + greeting now translate with everything else",
    "Subtle 'Translating…' indicator so language changes never look frozen",
  ],
  "1.0.5": [
    "Critical fix: Saturday Gospel and other music rails on Home now actually open the right track (route bug — was bouncing back to Home)",
    "Smoother handoff: tap any thumbnail and get an instant 'Opening…' indicator while the player loads",
    "Closing the player now clears the URL so re-tapping a different track opens cleanly",
  ],
  "1.0.4": [
    "Bible Precepts now span the whole 1611 KJV — 917 total (was 602)",
    "Tap any music video for a one-tap unmute overlay — no more silent play",
    "Mini player now hides cleanly with a tap, keeps playing in the background",
    "Nightly Lord's Prayer by text — opt in from Settings",
    "Faster language switching — translations cached on your device",
    "Sermon thumbnails no longer show broken-image icons on slow data",
  ],
  "1.0.3": [
    "Dynamic content now translates too — devotionals, history, news",
    "Concordance, Bible Dictionary, and Precepts read in your language",
    "Weekly rhythm + tonight's picks translate automatically",
    "Closed captions follow your language with no extra setup",
    "Faster + smoother text translation everywhere",
  ],
  "1.0.2": [
    "App now reads in 10 languages — pick yours from the flag chip",
    "Closed captions follow your selected language",
    "Bible verses translate cleanly (no more quota warnings)",
    "Auto-detects your phone's language on first launch",
    "Pulsing dot on the flag confirms your language is active",
  ],
  "1.0.1": [
    "Up/down floating arrows on every screen for fast scrolling",
    "Phone videos now upload in chunks — no more 100 MB cap",
    "Black-American canon rail moved below the Featured Room",
    "Better error messages when uploads or AI features hiccup",
    "Owner alert banner when AI credits run low",
  ],
};

// LocalStorage key for tracking which version a device has already
// acknowledged. Bumping APP_VERSION re-fires the toast for everyone.
export const LAST_SEEN_VERSION_KEY = "hw:last-seen-app-version";
